import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Flame, Activity, TrendingDown } from "lucide-react";

export default function WorldRecords() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/world-records"); setData(r.data); }
      catch { setData(null); }
    })();
  }, []);

  return (
    <div>
      <span className="pill">World Records</span>
      <h1 className="mt-2 text-3xl font-bold">Sonote community champions.</h1>
      <p className="text-zinc-500 mt-1">Anonymized leaderboards from sonographers worldwide. Updated daily.</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        <Stat icon={Activity} label="Total entries (90d)" val={data?.totals?.total_entries_90d ?? 0} tid="wr-total-entries" />
        <Stat icon={Trophy} label="Active sonographers" val={data?.totals?.active_sonographers ?? 0} tid="wr-active" />
        <Stat icon={TrendingDown} label="Total hours scanned" val={`${data?.totals?.total_hours_scanned ?? 0}h`} tid="wr-hours" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Board title="Longest tracking streak" icon={Flame} rows={data?.longest_streak || []} valKey="streak" suffix=" days" tid="wr-board-streak" />
        <Board title="Most consistent (entries / 90d)" icon={Activity} rows={data?.most_consistent || []} valKey="entries" tid="wr-board-consistent" />
        <Board title="Most improved (avg pain ↓)" icon={TrendingDown} rows={data?.most_improved || []} valKey="improvement" suffix=" pts" tid="wr-board-improved" />
      </div>

      <div className="card-elev mt-8 grain bg-zinc-900 text-white border-zinc-900" data-testid="wr-challenge">
        <span className="pill" style={{ background: "rgba(212,175,55,0.18)", color: "var(--gold)" }}>This month&apos;s challenge</span>
        <h2 className="text-2xl font-bold mt-3">The 21-Day Streak Challenge</h2>
        <p className="mt-2 text-zinc-300 max-w-2xl">Log a daily check-in for 21 consecutive days and earn the &quot;Steady Hands&quot; badge.
        Top 100 finishers get a feature in the Sonote community quarterly.</p>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, val, tid }) {
  return (
    <div className="card-elev" data-testid={tid}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.15em] font-bold">
        <Icon className="w-4 h-4" style={{ color: "var(--gold)" }} /> {label}
      </div>
      <div className="mt-3 text-3xl font-extrabold">{val}</div>
    </div>
  );
}

function Board({ title, icon: Icon, rows, valKey, suffix = "", tid }) {
  return (
    <div className="card-elev" data-testid={tid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5" style={{ color: "var(--gold)" }} />
        <h3 className="font-bold">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">Not enough data yet.</p>
      ) : (
        <ol className="space-y-3">
          {rows.slice(0, 10).map((r, i) => (
            <li key={r.user_id} className={`flex items-center justify-between gap-3 ${r.is_me ? "rounded-lg p-2 -m-2" : ""}`} style={r.is_me ? { background: "var(--gold-light)" } : {}}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${i < 3 ? "text-white" : "bg-zinc-100 text-zinc-700"}`} style={i < 3 ? { background: "var(--gold)" } : {}}>{i + 1}</span>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{r.alias} {r.is_me && <span className="text-xs ml-1" style={{ color: "var(--gold-hover)" }}>(you)</span>}</div>
                  <div className="text-xs text-zinc-500 truncate">{r.clinic}</div>
                </div>
              </div>
              <div className="font-bold tabular-nums">{r[valKey]}{suffix}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
